import {
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useToggle from '@stripcash/ui-kit/utils/hooks/useToggle';

import { Category } from 'common/selectors/sidebar/utils';
import { CommonUnionState } from 'common/reducers';
import useClickOutside from 'common/helpers/useClickOutside';
import {
  collapseSidebarAction,
  expandSidebarAction,
} from 'common/reducers/sidebar';
import { useShortcuts } from 'common/layouts/ShortcutsListener';
import { KEY_CODES } from 'common/layouts/ShortcutsListener/constants';

import getDefaultActiveGroup from './utils/getDefaultActiveGroup';
import SidebarView from './components/SidebarView';

type SidebarProps = PropsWithChildren<{
  structure: Category[];
}>;

const Sidebar = ({ structure }: SidebarProps) => {
  const { pathname } = window.location;
  const dispatch = useDispatch();
  const [activeGroup, setActiveGroup] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [isShortcutsReady, toggleReadyShortcuts] = useToggle(false);
  const sidebarRef = useRef<HTMLElement | null>(null);
  const toggleReg = useRef<HTMLElement | null>(null);

  const { isSidebarShown, dangers, isMobile } = useSelector(
    ({ sidebar, ui }: CommonUnionState) => ({
      ...sidebar,
      isMobile: ui.breakpoint < 1200,
    }),
  );

  useShortcuts(
    [
      {
        keyCombo: [KEY_CODES.MetaKey, KEY_CODES.KeyB],
        action: useCallback(() => {
          if (isSidebarShown) {
            dispatch(collapseSidebarAction());
          } else {
            dispatch(expandSidebarAction());
          }
        }, [dispatch, isSidebarShown]),
      },
    ],
    isShortcutsReady,
  );

  const close = () => {
    dispatch(collapseSidebarAction());
  };

  const handleClose = useCallback(() => {
    if (isSidebarShown) {
      close();
    }
  }, [close, isSidebarShown]);

  const handleChangeExpandedGroups = useCallback((group: string) => {
    setExpandedGroups((prevGroups) =>
      prevGroups.includes(group)
        ? prevGroups.filter((prevGroup) => prevGroup !== group)
        : [...prevGroups, group],
    );
  }, []);

  useEffect(() => {
    toggleReg.current = document.getElementById('menu__toggle')!.parentElement;
  }, []);

  useEffect(close, [dispatch, pathname]);

  useClickOutside([sidebarRef, toggleReg], handleClose);

  useEffect(() => {
    const { path, title } = getDefaultActiveGroup(pathname, structure);

    setActiveGroup(path);
    setExpandedGroups((prev) => prev.concat(title[0]));
  }, [pathname, structure]);

  const [collapsed, toggleCollapsed, setCollapsed] = useToggle(false);

  useEffect(() => {
    setCollapsed(false);
  }, [isMobile, setCollapsed]);

  useEffect(() => {
    toggleReadyShortcuts();
  }, [toggleReadyShortcuts]);

  return (
    <SidebarView
      ref={sidebarRef}
      isSidebarShown={isSidebarShown}
      collapsed={collapsed}
      structure={structure}
      activeGroup={activeGroup}
      pathname={pathname}
      dangers={dangers}
      toggleCollapsed={toggleCollapsed}
      isMobile={isMobile}
      expandedGroups={expandedGroups}
      onExpand={handleChangeExpandedGroups}
    />
  );
};

export default Sidebar;
